import axios, { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import BodyWrap from '../../components/BodyWrap';
import { useNavigate } from 'react-router-dom';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { IconCarambola, IconCheck, IconRocket, IconX } from '@tabler/icons-react';
import { PRICING_MATRIX } from '../../constants/billing';
import { Modal, Switch, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { CurrentPlan, PricingMatrix } from '../../types/Pricing';
import { clearLocalStorage } from '../../utils/authUtils';
import { GradientButton } from '../../components/Buttons';

function BillingSettings() {
  useEffect(() => {
    document.title = 'Account & Security | PromptSlate';
  }, []);
  const navigate = useNavigate();
  const [paddle, setPaddle] = useState<Paddle>();
  let [billAnnually, setBillAnnually] = useState<boolean>(true);
  let [getDataMessage, setGetDataMessage] = useState<string>('Loading plan details...');
  let [newPriceId, setNewPriceId] = useState<string>('');
  let [newPlan, setNewPlan] = useState<PricingMatrix>();
  const [confirmPlanChangeModalOpened, setConfirmPlanChangeModalOpened] = useState(false);
  let [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    initializePaddle({
      environment: 'sandbox',
      token: 'test_086c49a74e960a7a0ae071d2ff1',
      eventCallback: function (data) {
        if (data.name == 'checkout.completed') {
          refetchCurrentPlanData();
        }
      },
    }).then((paddleInstance: Paddle | undefined) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
      }
    });
  }, []);

  const {
    error: keyFetchError,
    data: currentPlan,
    refetch: refetchCurrentPlanData,
  } = useQuery({
    queryKey: ['currentPlan'],
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CurrentPlan | null> => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/billing/current-plan`,
            {
              headers: headers,
            }
          );
          const currentPlan: CurrentPlan = response.data;
          return currentPlan;
        } else {
          return null;
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setGetDataMessage('Unable to fetch plan details');
          } else {
            setGetDataMessage('A network error occured. Please try again later.');
          }
          return null;
        } else {
          setGetDataMessage('An error occured. Please try again later.');
          return null;
        }
      }
    },
  });

  const handlePlanClick = (priceId: string) => {
    if (!currentPlan?.company_id) {
      const email = localStorage.getItem('email');
      const companyId = localStorage.getItem('companyId');
      if (!email || !companyId) {
        return;
      }
      paddle?.Checkout.open({
        items: [{ priceId: priceId, quantity: 1 }],
        customer: {
          email: email,
        },
        customData: {
          company_id: companyId,
        },
      });
    } else {
      const newPlan = PRICING_MATRIX.find(
        (pricing) =>
          pricing.pricing.monthly.priceId === priceId || pricing.pricing.annualy.priceId === priceId
      );
      if (!newPlan) {
        return;
      }
      setNewPlan(newPlan);
      setNewPriceId(priceId);
      setConfirmPlanChangeModalOpened(true);
    }
  };

  const handleModifyPlanSubmit = async () => {
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/billing/modify`,
        {
          price_id: newPriceId,
        },
        {
          headers: headers,
        }
      );
      refetchCurrentPlanData();
      setIsLoading(false);
      setConfirmPlanChangeModalOpened(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
        } else {
        }
      } else {
        // setEnvCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  const renderCurrentPlan = () => {
    if (currentPlan) {
      if (currentPlan.status == 'NO_SUBSCRIPTION') {
        return <div>No active subscription. Please choose from the below plans.</div>;
      } else {
        const currentPlanData = PRICING_MATRIX.find(
          (pricing) => pricing.title.toLowerCase() === currentPlan.plan
        );
        if (!currentPlanData) {
          return <span>Error loading current plan</span>;
        }
        return (
          <div className="flex flex-row justify-between border rounded-lg p-4 items-center cursor-pointer mb-5">
            <div className="flex flex-col">
              <span className="text-lg">{currentPlanData.title}</span>
              {currentPlanData.features.map((feature) => (
                <span className="text-sm text-slate-500">{feature}</span>
              ))}
            </div>
            <div className="flex flex-col">
              <span className="text-lg">
                {currentPlan.billing_period === 'annual'
                  ? currentPlanData.pricing.annualy.pricePerMonth
                  : currentPlanData.pricing.monthly.pricePerMonth}
                /mo
              </span>
              <span className="text-sm  text-slate-500">
                billed {currentPlan.billing_period === 'annual' ? 'annually' : 'monthly'}
              </span>
            </div>
          </div>
        );
      }
    } else {
      return <div>Loading current plan...</div>;
    }
  };

  return (
    <BodyWrap>
      <Modal
        opened={confirmPlanChangeModalOpened}
        onClose={() => setConfirmPlanChangeModalOpened(false)}
        title={<Text size={'xl'}>Confirm subscription change</Text>}
        size={'lg'}
        radius="lg"
      >
        <div className="flex flex-col">
          <p>
            By continuing, your plan will be modified to <b>{newPlan?.title}</b> and will be billed
            on <b>{newPlan?.pricing.annualy.priceId == newPriceId ? `an annual` : 'a monthly'}</b>{' '}
            basis. Please make sure you have selected a plan that best suits your requirements
            inorder to avoid any service disruption before continuing.
          </p>
          <GradientButton
            className="mt-8 mb-5"
            type="submit"
            disabled={isLoading}
            onClick={handleModifyPlanSubmit}
          >
            {isLoading ? 'Loading...' : 'Modify plan'}
          </GradientButton>
        </div>
      </Modal>
      <div className="text-xl pb-5 ">Billing</div>
      {currentPlan ? (
        <div className="w-full h-fit flex flex-col gap-8 items-center mt-8">
          <div className="max-w-[800px] w-full bg-white rounded-lg border">
            <div className="text-lg flex flex-row items-center h-[60px] p-5 border-b">
              <IconCarambola className="mr-2" /> Current Plan
            </div>
            <div className="flex flex-col p-5">{renderCurrentPlan()}</div>
          </div>
          <div className="max-w-[800px] w-full bg-white rounded-lg border">
            <div className="text-lg flex flex-row items-center h-[60px] p-5 border-b justify-between">
              <div className="flex flex-row">
                <IconRocket className="mr-2" /> Upgrade Plan
              </div>
              <Switch
                checked={billAnnually}
                onChange={(event) => setBillAnnually(event.currentTarget.checked)}
                color="teal"
                size="md"
                label="Annual billing"
                thumbIcon={
                  billAnnually ? (
                    <IconCheck size={12} color="var(--mantine-color-teal-6)" stroke={3} />
                  ) : (
                    <IconX size={12} color="var(--mantine-color-red-6)" stroke={3} />
                  )
                }
              />
            </div>
            <div className="flex flex-col p-5">
              {PRICING_MATRIX.map((pricing) => (
                <div
                  onClick={() =>
                    handlePlanClick(
                      billAnnually
                        ? pricing.pricing.annualy.priceId
                        : pricing.pricing.monthly.priceId
                    )
                  }
                  className="flex flex-row justify-between border rounded-lg p-4 items-center cursor-pointer mb-5"
                  key={pricing.order}
                >
                  <div className="flex flex-col">
                    <span className="text-lg">{pricing.title}</span>
                    {pricing.features.map((feature) => (
                      <span className="text-sm text-slate-500">{feature}</span>
                    ))}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-lg">
                      {billAnnually
                        ? pricing.pricing.annualy.pricePerMonth
                        : pricing.pricing.monthly.pricePerMonth}
                      /mo
                    </span>
                    <span className="text-sm  text-slate-500">
                      billed {billAnnually ? 'annually' : 'monthly'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div>{getDataMessage}</div>
      )}
    </BodyWrap>
  );
}

export default BillingSettings;
